import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, CircularProgress, Grid, FormControlLabel, Checkbox, MenuItem } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import { fetchPartTercAttributeById, createPartTercAttribute, updatePartTercAttribute, setSelectedItem, clearValidationErrors, setError, clearSuccessMessage } from '../../store/reducers/partTercAttributesSlice';
import { PartTercAttribute } from '../../services/partTercAttributesApi';
import { partSecAttributesApi, PartSecAttribute } from '../../services/partSecAttributesApi';

const useStyles = makeStyles((theme) => ({ root: { padding: theme.spacing(3) }, paper: { padding: theme.spacing(3), marginBottom: theme.spacing(3) }, formControl: { marginBottom: theme.spacing(2) }, buttonGroup: { marginTop: theme.spacing(3), display: 'flex', gap: theme.spacing(1) }, loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 } }));

const PartTercAttributesEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { selectedItem, loading, error, validationErrors, successMessage } = useSelector((s: RootState) => s.partTercAttributes);

  const [formData, setFormData] = useState<Partial<PartTercAttribute>>({ PartSecAttributeId: null as any, Name: '', Description: '', Active: true });
  const [partSecAttributes, setPartSecAttributes] = useState<PartSecAttribute[]>([]);

  useEffect(() => { (async () => { try { const resp = await partSecAttributesApi.getAll({ page: 1, pageSize: 500 }); const data = (resp as any).data || (resp as any).items || []; setPartSecAttributes(data); } catch {} })(); }, []);

  useEffect(() => {
    if (id) dispatch(fetchPartTercAttributeById(parseInt(id))); else { dispatch(setSelectedItem(null)); setFormData({ PartSecAttributeId: null as any, Name: '', Description: '', Active: true }); }
    dispatch(clearValidationErrors()); dispatch(setError(null)); dispatch(clearSuccessMessage());
  }, [id, dispatch]);

  useEffect(() => { if (selectedItem && id) setFormData({ PartSecAttributeId: selectedItem.PartSecAttributeId, Name: selectedItem.Name, Description: selectedItem.Description || '', Active: selectedItem.Active }); }, [selectedItem, id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target; const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (validationErrors.length) dispatch(clearValidationErrors()); if (error) dispatch(setError(null));
  };

  const handleSave = () => { const payload = { ...formData } as Omit<PartTercAttribute, 'Id'>; if (id) dispatch(updatePartTercAttribute(parseInt(id), payload)); else dispatch(createPartTercAttribute(payload)); };
  const handleCancel = () => navigate('/catalogs/parttercattributes');

  if (loading && (id && !selectedItem)) return (<Box className={classes.loadingContainer}><CircularProgress /></Box>);

  return (
    <Box className={classes.root}>
      <Typography variant="h4" gutterBottom>{id ? 'Edit Part Tertiary Attribute' : 'Add New Part Tertiary Attribute'}</Typography>
      {error && (<Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>)}
      {validationErrors && validationErrors.length > 0 && (<Alert severity="error" style={{ marginBottom: 16 }}>{validationErrors.map((err, idx) => (<div key={idx}>{err}</div>))}</Alert>)}
      {successMessage && (<Alert severity="success" style={{ marginBottom: 16 }}>{successMessage}</Alert>)}
      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth label="Parent Secondary Attribute" name="PartSecAttributeId" value={formData.PartSecAttributeId ?? ''} onChange={handleInputChange} required className={classes.formControl} helperText="Select the parent Secondary Attribute (required)">
              {partSecAttributes.map((p) => (<MenuItem key={p.Id} value={p.Id!}>{p.Name}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel control={<Checkbox name="Active" checked={!!formData.Active} onChange={handleInputChange} color="primary" />} label="Active" className={classes.formControl} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Name" name="Name" value={formData.Name || ''} onChange={handleInputChange} required className={classes.formControl} helperText="Enter the name (required)" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" name="Description" value={formData.Description || ''} onChange={handleInputChange} multiline rows={3} className={classes.formControl} helperText="Optional description" />
          </Grid>
        </Grid>
        <Box className={classes.buttonGroup}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
          <Button variant="outlined" onClick={handleCancel} disabled={loading}>Cancel</Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PartTercAttributesEdit; 